import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { Observable } from "rxjs";
import { of } from "rxjs";
import { delay, map } from "rxjs/operators";
// TAG_DB import removed — the 10,000-record mock was loaded into the bundle
// even when useMockData = false. The mock getTags path below returns an empty
// page so the runtime contract stays intact if useMockData is ever re-enabled.
import { ApiService } from "src/app/services/api.service";

export interface TagItem {
  id: number;
  name: string;
  type: string; // patient-id | prescriber-id | account-id
}

export interface TagPage {
  items: TagItem[];
  total: number;
}
@Injectable({
  providedIn: "root",
})
export class TaskpanelService {
  private _isOpen$ = new BehaviorSubject<boolean>(false);
  isOpen$ = this._isOpen$.asObservable();

  /** Cross-component bridge: emits a task when another part of the app (e.g. the
   *  patient grid's "Cases" action) wants the Case Management board to open its
   *  subtask sidebar for that task. `TaskManagementWfComponent` subscribes and
   *  forwards the task to its own `onKanbanCardClick`. */
  private _openCaseSidebar$ = new Subject<any>();
  openCaseSidebar$ = this._openCaseSidebar$.asObservable();

  private useMockData = false;

  constructor(private apiService: ApiService) {}

  open() {
    this._isOpen$.next(true);
  }

  close() {
    this._isOpen$.next(false);
  }

  toggle() {
    this._isOpen$.next(!this._isOpen$.value);
  }

  /** Ask the Case Management board to open its subtask sidebar for `task`.
   *  Listened to by `TaskManagementWfComponent`. */
  openCaseSidebar(task: any) {
    this._openCaseSidebar$.next(task);
  }

  /** Search tags within a tag category by keyword. */
  searchTagsByCategory(tagCategoryId: number, keyword: string): Observable<any> {
    return this.apiService.post('api/tasks/tags/get-by-category', {
      tag_category_id: tagCategoryId,
      keyword: keyword,
    });
  }

  /** Fetch the full list of tag categories (dynamic IDs starting at 2000).
   *  Drives the tag-type chip selector in the main task dialog. */
  getTagCategories(): Observable<any> {
    return this.apiService.post('api/tag-categories/get-all', {});
  }

  /** Lazy entity search used by the tag-type chip + keyword flow in the main
   *  task dialog. Only called when the user has typed >=2 characters. */
  searchEntities(category: string, keyword: string, brandKey: number | string): Observable<any> {
    return this.apiService.post('api/tasks/tags/search-entities', {
      category: category,
      keyword: (keyword || '').trim(),
      brand_key: String(brandKey),
    });
  }

  getTags(
    tagType: string,
    page: number,
    pageSize: number,
    searchTerm: string = ""
  ): Observable<TagPage> {
    if (this.useMockData) {
      // Mock path — TAG_DB import removed to avoid loading 10K records into
      // the bundle. Return an empty page so the runtime contract holds.
      return of({ items: [], total: 0 }).pipe(delay(200));
    }

    // Real API path. tagType strings ("patient-id" / "prescriber-id" / "account-id")
    // map to tag_category_id 1/2/3 in the same order as the existing taskTypeMap.
    const categoryIdMap: Record<string, number> = {
      'patient-id': 1,
      'prescriber-id': 2,
      'account-id': 3,
    };
    const tagCategoryId = categoryIdMap[tagType] ?? 0;

    return this.searchTagsByCategory(tagCategoryId, searchTerm).pipe(
      map((res: any) => this.mapApiResponseToTagPage(res, tagType, page, pageSize))
    );
  }

  /** Map a tag-search API response to the existing TagPage shape (with client-side paging). */
  private mapApiResponseToTagPage(res: any, tagType: string, page: number, pageSize: number): TagPage {
    let items: any[] = [];
    if (Array.isArray(res)) {
      items = res;
    } else if (Array.isArray(res?.data)) {
      items = res.data;
    } else if (Array.isArray(res?.result)) {
      items = res.result;
    } else if (Array.isArray(res?.tags)) {
      items = res.tags;
    }

    const all: TagItem[] = items.map((t: any) => ({
      id: t?.tag_id ?? t?.id ?? 0,
      name: t?.tag_name ?? t?.name ?? '',
      type: t?.type ?? tagType,
    }));

    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return {
      items: all.slice(start, end),
      total: all.length,
    };
  }
}
